import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "../data/site";


const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/xeriscape", label: "Xeriscape" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const on = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        ticking = false;
      });
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors ${
        scrolled
          ? "bg-white border-b border-stone-200 shadow-sm"
          : "bg-white border-b border-stone-200/60"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" data-testid="logo-link" aria-label="Texan Strong Builds — home" className="flex items-center gap-3 md:gap-4">
            <img src={COMPANY.logoSmall} alt="Texan Strong Builds" width="240" height="128" className="h-12 md:h-14 w-auto object-contain mix-blend-multiply" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display font-black text-stone-900 text-lg md:text-xl tracking-tight">
                TEXAN STRONG BUILDS
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">
                Concrete · Landscape · Austin TX
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-red-600" : "text-stone-700 hover:text-stone-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={COMPANY.phoneTel}
              data-testid="header-call-link"
              className="flex items-center gap-2 text-sm font-bold text-stone-900 hover:text-red-600"
            >
              <Phone size={16} strokeWidth={2.4} />
              {COMPANY.phone}
            </a>
            <Link to="/contact" data-testid="header-cta-estimate" className="btn-primary text-sm py-3 px-5">
              Free Estimate
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-stone-900"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-stone-200" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 px-2 text-base font-medium border-b border-stone-100 ${
                    isActive ? "text-red-600" : "text-stone-800"
                  }`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 pt-4">
              <a href={COMPANY.phoneTel} className="btn-outline w-full" data-testid="mobile-call-btn">
                <Phone size={16} /> Call {COMPANY.phone}
              </a>
              <Link to="/contact" className="btn-primary w-full" data-testid="mobile-estimate-btn">
                Get Free Estimate
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
